import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { apiRequest } from '../api';
import { ABG } from './abg';
import API_BASE_URL from '../apiConfig';
import './RPSGame.css';
import audioManager from './AudioManager'; 
import { commonIcons, rpsAssets, getFlag } from './assetImports';
import { useTranslation } from 'react-i18next';

const RPSGame = ({ onRematch }) => {
    const { t } = useTranslation('games');
    const { socket, user, updateUserDetails } = useContext(AuthContext);

    // State variables
    const [game, setGame] = useState(null);
    const navigate = useNavigate();
    const [opponents, setOpponents] = useState([]);
    const [isGameEnd, setIsGameEnd] = useState(false);
    const [playerMove, setPlayerMove] = useState(null);
    const [isMoveLoading, setIsMoveLoading] = useState(false);
    const [opponentMove, setOpponentMove] = useState(null);
    const [showMoveButtons, setShowMoveButtons] = useState(false);
    const [currentRound, setCurrentRound] = useState(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [roundResults, setRoundResults] = useState([]);
    const [animateRound, setAnimateRound] = useState(false);
    const [ShowCancelPopup, setShowCancelPopup] = useState(false);
    const [countdown, setCountdown] = useState(15);
    const [isWaitingForRematch, setIsWaitingForRematch] = useState(false);
    const [isSoundOn, setIsSoundOn] = useState(true); 
    const DEFAULT_VOLUME = 0.8; // Set this to the desired default volume
    audioManager.setSoundOn(isSoundOn);
    
    const [rpsShowTextMenu, setRpsShowTextMenu] = useState(false);
    const [rpsShowSettingsMenu, setRpsShowSettingsMenu] = useState(false);
    const [rpsPlayerMessage, setRpsPlayerMessage] = useState(null);
    const [rpsOpponentMessage, setRpsOpponentMessage] = useState(null);
    const [isLeaveGamePopupVisible, setIsLeaveGamePopupVisible] = useState(false);





    const calculateAnimationPositions = useCallback(() => {
        const viewportHeight = window.innerHeight;
        const meetPointY = viewportHeight * 0.45; 
        const offset = 130;
       
      
        
        const playerStartFromTop = viewportHeight - 120;
        const opponentStartFromTop =  65;
        
        const playerMovement = (playerStartFromTop - meetPointY) - offset;
        const opponentMovement = meetPointY - offset - opponentStartFromTop;
        
        document.documentElement.style.setProperty('--player-movement', `-${playerMovement}px`);
        document.documentElement.style.setProperty('--opponent-movement', `${opponentMovement}px`);
        
      }, []);

      useEffect(() => {
        const handleResize = () => {
          if (playerMove && opponentMove) {
            calculateAnimationPositions();
          }
        };
        
        handleResize(); // Initial call
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
      }, [playerMove, opponentMove, calculateAnimationPositions]);

    useEffect(() => {
        if (!socket) return;
    
        socket.on('rps-message', ({ message, userID }) => {
            if(user._id !== userID) {
            setRpsOpponentMessage(t(`quickChat.${message}`));
            setTimeout(() => setRpsOpponentMessage(null), 2500);
          }
        });
    
        return () => {
          socket.off('rps-message');
        };
      }, [socket, game, user]);
  

    function playRpsSound(soundName) {
        audioManager.setVolume(DEFAULT_VOLUME);
        audioManager.playSound(soundName);
    }

    useEffect(() => {
        if (currentRound !== null && currentRound>1) {
          
            setAnimateRound(true);
            const timer = setTimeout(() => setAnimateRound(false), 2400); 
            return () => clearTimeout(timer); 
        }
    }, [currentRound]);

    useEffect(() => {
        socket.on('game-replay', onRematch);
        return () => socket.off('game-replay', onRematch);
      }, [onRematch]);

   useEffect(() => {
           const handleTouchStart = (event) => {
               event.preventDefault(); 
           };
       
          
       
           document.addEventListener("pointerdown", handleTouchStart, { passive: false });
           document.addEventListener("touchstart", handleTouchStart, { passive: false });
       
           return () => {
               document.removeEventListener("pointerdown", handleTouchStart);
               document.removeEventListener("touchstart", handleTouchStart);
           };
       }, []);
    

   useEffect(() => {
    let timer;
    if (countdown > 0 && !playerMove) {
        timer = setInterval(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);
    }

    return () => clearInterval(timer);
    }, [countdown, playerMove]);



    const fetchGameState = useCallback(async () => {
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/current-game`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();

            if (response.ok && data.game) {
                if (data.game.status === 'completed') {
                    navigate('/game');
                    return; 
                }
            
                
                setGame(data.game);
                setCurrentRound(data.game.currentRound);
                setOpponents(data.opponents || []);
                handleGamePhase(data.game);
            } else {
                setGame(null);
                setPopupMessage('Failed to fetch the current game state.');
                navigate('/game');
                return;
            
            }
        } catch (error) {
            console.error('Error fetching game state:', error);
            setGame(null);
            navigate('/game');
            return;
        }
    }, [navigate]);

    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        socket.on('connect', () => {
            fetchGameState();
        });
        socket.on('game-update', (data) => {
                     
            if (!data.game) {
                navigate('/game');
                return; 
            }


            setGame(data.game);
            setCurrentRound(data.game.currentRound);
            setOpponents(data.opponents || []);
            handleGamePhase(data.game);
        });

        return () => {
            socket.off('game-update');
            socket.off('connect');
        };
    }, [socket, user, navigate]);


    const handleGamePhase = (gameData) => {
        switch (gameData.phase) {
            case 'choose':
                case 'choose':
                setPlayerMove(null);
                setOpponentMove(null);
                setCurrentRound(gameData.currentRound);
                
                
            break;

            case 'waiting-for-opponent':
            const playerChoice = gameData.choices[user._id];
            if (playerChoice) {
                setPlayerMove(playerChoice);
                
            }
            break;


            case 'round-completed': {
                if (gameData.choices && gameData.userIds?.length === 2) {
       
                    const [player1Id, player2Id] = gameData.userIds;
            
                
                    const playerPlayedMove = gameData.choices[user._id]; 
                    const opponentPlayedMove =
                        player1Id === user._id
                            ? gameData.choices[player2Id] 
                            : gameData.choices[player1Id];
           
                    setPlayerMove(playerPlayedMove);
                    setOpponentMove(opponentPlayedMove);
                    setCountdown(15);
                   
                }
            
           
            
                break;
            }
            
            case 'game-canceled': {
                setPlayerMove(null);
                setOpponentMove(null);
                setShowCancelPopup(true); 
                setTimeout(() => {
                    setShowCancelPopup(false);
                    navigate('/game'); 
                }, 4000);
                break;
            }
            

            
            case 'game-completed':
                
                setIsGameEnd(true);
          
                break;
                
            default:
                console.warn(`Unhandled game phase: ${gameData.phase}`);
        }
    };
    


    const handlePlayerChoice = async (e, choice) => {
        e.preventDefault(); 
        playRpsSound('buttonClick');
        setPlayerMove(choice); 
      
        setIsMoveLoading(true);
    
        try {
            const response = await apiRequest(`${API_BASE_URL}/api/rps-move`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    gameId: game._id,
                    choice: choice,
                }),
            });
    
            if (!response.ok) throw new Error('Failed to make a move.');
           
        } catch (error) {
            console.error('Error sending move:', error);
        }finally {
            setIsMoveLoading(false);
        }
    };

   
    
    
useEffect(() => {
    const handleVisibilityChange = () => {
        if (!document.hidden) {
            fetchGameState();
        }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
}, [fetchGameState]); 
   

    useEffect(() => {
        fetchGameState();
    }, [fetchGameState]);



    

const triggerConfettiAnimation = () => {
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';

    for (let i = 0; i < 200; i++) { 
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        
      
        confetti.style.left = '50%';
        confetti.style.top = '50%'; 
        confetti.style.backgroundColor = `hsl(${Math.random() * 360}, 100%, 50%)`; 
        confetti.style.transform = `rotate(${Math.random() * 360}deg)`; 

  
        const randomX = (Math.random() - 0.5) * window.innerWidth * 2; 
        const randomY = (Math.random() - 0.5) * window.innerHeight * 2;
        const duration = Math.random() * 1.4 + 0.8; 

        confetti.style.animation = `explode ${duration}s ease-out forwards`;
        confetti.style.setProperty('--randomX', `${randomX}px`); 
        confetti.style.setProperty('--randomY', `${randomY}px`); 

        confettiContainer.appendChild(confetti);
    }

    document.body.appendChild(confettiContainer);

    setTimeout(() => {
        document.body.removeChild(confettiContainer);
    }, 4000);  
};

useEffect(() => {
    if (game && user && isGameEnd) {
        if (game.winners?.includes(user._id)) {
            triggerConfettiAnimation();
            playRpsSound('win'); 
        } else {
            playRpsSound('lose'); 
        }
    }
}, [isGameEnd, game, user._id, navigate]);
    
const [showBonusDetails, setShowBonusDetails] = useState(false);
  useEffect(() => {
        if (isGameEnd) {
            updateUserDetails();
        }
    }, [isGameEnd]);
    
const renderGameEndPopup = () => {
  if (!isGameEnd || !game || !game.players) return null;

  const currentPlayer = game.players.find(player => 
    player.userId.toString() === user._id.toString()
  );
  
  if (!currentPlayer) return null;

  const winnersArray = game.winners || [];
  const winnerId = winnersArray[0] || null;
  const isUserWinner = winnerId === user._id;
  const currency = currentPlayer.currency;
  const betAmount = currentPlayer.betAmount;
  const winnings = isUserWinner ? calculateWinnerGain(betAmount, currency) : 0;
  const endReason = game.endReason || null;

  // Check if player hasn't completed 10 games today
  const gamesPlayedToday = user.dailyGameStats?.[`${currency}GamesPlayed`] || 0;
  const gamesRemaining = Math.max(0, 10 - gamesPlayedToday);
  const showBonusOffer = gamesRemaining > 0;
  

  const getCurrencyIcon = () => {
    switch(currency) {
      case 'token':
        return <div className={`rps-${isUserWinner ? 'Win' : 'Lose'}TokenCoin-icon`}></div>;
      case 'stars':
        return <div className="rps-star-icon"></div>;
      case 'usdt':
        return <div className="rps-dollar-sign-icon">$</div>;
      default:
        return null;
    }
  };

    const getCurrencyBonusIcon = () => {
    switch(currency) {
      case 'token':
        return <div className="rps-tokenYellow-icon"></div>;
      case 'stars':
        return <div className="rps-star-icon"></div>;
      case 'usdt':
        return <div className="rps-dollar-sign-icon">$</div>;
      default:
        return null;
    }
  };

  const getBonusReward = () => {
    switch(currency) {
      case 'token': return 100;
      case 'stars': return 500;
      case 'usdt': return 10;
      default: return 0;
    }
  };

  const handleGetBonus = () => {
    setShowBonusDetails(true);
  };

  return (
    <>
      <div className="rps-popup-overlay"></div>
      <div className="rps-end-game-popup">
        {isUserWinner && !isWaitingForRematch && <div className="rps-win-background-animated"></div>}
        <div className="rps-screen-blur"></div>
        <div className="rps-popup-background"></div>

        {!isWaitingForRematch && (
          <div className="rps-popup-content">
            {!showBonusDetails ? (
              <>
                <h2 className="rps-popup-title">
                  {isUserWinner 
                    ? (!endReason ? t('popup.youWon') : t('popup.opponentLeft'))
                    : t('popup.youLose')}
                </h2>

                <div className="rps-points-change">
                  {isUserWinner ? (
                    <>
                      <span className="rps-points-positive">
                        <span className="rps-sign">+</span>
                        <span className="rps-number">{winnings}</span>
                      </span>
                      {getCurrencyIcon()}
                    </>
                  ) : (
                    <>
                      <span className="rps-points-negative">
                        <span className="rps-sign">-</span>
                        <span className="rps-number">{betAmount}</span>
                      </span>
                      {getCurrencyIcon()}
                    </>
                  )}
                </div>

                <div className="rps-total-score">
                  {t('labels.totalScore')}: <span>{user.score}</span>
                </div>

                <div className="rps-popup-buttons">
                  <button 
                    className="rps-exit-btn" 
                    onPointerUp={(e) => handleExit(e)}
                  />
                  
                  {showBonusOffer && (
                    <button 
                      className="rps-get-bonus-btn" 
                      onPointerUp={handleGetBonus}
                    >
                      {t('buttons.getBonus')}
                    </button>
                  )}

                  {!showBonusOffer && (
                    <button 
                      className="rps-replay-btn" 
                      onPointerUp={(e) => handleReplay(e, game, user._id)}
                    >
                      {t('buttons.replay')}
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <h2 className="rps-popup-title-small">
                  {t('popup.playMoreGames', { count: gamesRemaining })}
                </h2>

                <div className="rps-points-change-bonus">
                  <span className="rps-points-bonus">
                    <span className="rps-sign">+</span>
                    <span className="rps-number">{getBonusReward()}</span>
                  </span>
                  {getCurrencyBonusIcon()}
                </div>

                <div className="rps-popup-buttons">
                  <button 
                    className="rps-exit-btn" 
                    onPointerUp={(e) => handleExit(e)}
                  />
                  
                  <button 
                    className="rps-play-btn" 
                    onPointerUp={(e) => handleReplay(e, game, user._id)}
                  >
                    {t('buttons.play')}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {isWaitingForRematch && (
          <div className="rps-popup-loading">
            <div className="rps-loading-spinner"></div>
            <div className="rps-waiting-text">{t('popup.waitingForOpponent')}</div>
          </div>
        )}
      </div>
    </>
  );
};


const calculateWinnerGain = (betAmount, currency) => {
  const payoutMap = {
    token: {
      25: 25
    },
    stars: {
      300: 200,
      1200: 800,
      6000: 4000
    },
    usdt: {
      3: 2,  
      12: 8,
      60: 40
    }
  };
  

  if (!payoutMap[currency]) {
      console.error(`Unsupported currency: ${currency}`);
      return 0;
  }

  const gain = payoutMap[currency][betAmount];
  if (gain === undefined) {
      console.error(`Invalid bet amount: ${betAmount} for currency: ${currency}`);
      return 0;
  }

  return gain;
};
  
  

  const handleExit = (e) => {
    e.preventDefault(); 
    navigate('/game'); 
  };

  const handleReplay = async (e, currentGame, userid) => {
    e.preventDefault(); 
    if (!currentGame || !socket.id || !currentGame.players) return;
    
 
    const currentPlayer = currentGame.players.find(player => 
        player.userId.toString() === userid.toString() 
    );
    
    if (!currentPlayer) return;

    const gameMode = currentGame.gameMode;
    const socketId = socket.id;
    const amount = currentPlayer.betAmount;
    const currency = currentPlayer.currency;
    const gameId = currentGame._id;

    try {
        // 3. Use same start-game API with existing params
        const response = await apiRequest(`${API_BASE_URL}/api/start-game`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            betAmount: amount, 
            gameMode, 
            socketId, 
            isRematch: true,
            currency,
            isPrived: !!currentGame.isPrivate, 
            referrerId: currentGame.userIds?.[0] || null  
          })
        });
        const data = await response.json();
    
        if (response.ok) {
            setIsWaitingForRematch(true);
        } else {
            console.log("FAILED", data?.message);
        }
    } catch (err) {
        console.error("Rematch error:", err);
    };
};


const renderGameScore = () => {
    const playerScore = game.totalGamePoints[user._id] || 0;
    const opponent = opponents.find(o => o._id !== user._id);
    const opponentScore = game.totalGamePoints[opponent?._id] || 0;
    if(playerScore === 0 && opponentScore ===0) return null;
  
    return (
      <div className={`rps-score-display ${animateRound ? 'rps-animate' : ''}`}>
        <div className="rps-score-box">{playerScore}</div>
        <div className="rps-score-separator">:</div>
        <div className="rps-score-box">{opponentScore}</div>
      </div>
    );
  };
  

    const renderAvatar = (name, photoUrl, customAvatar) => {
      if (customAvatar) {
        return <img src={customAvatar} alt={`${name}'s Custom Avatar`} className="rps-avatar-image" />;
    }
        if (photoUrl) {
            return <img src={photoUrl} alt={`${name}'s Avatar`} className="rps-avatar-image" />;
        }
        const initial = name.charAt(0).toUpperCase(); 
        const avatarStyle = ABG(name);
        return <div className="rps-avatar-placeholder" style={avatarStyle}>{initial}</div>;
    };

      

    const renderOpponentArea = (game, user, opponents) => {
        const opponent = opponents.find(opponent => opponent._id !== user._id);
      
        if (!opponent) return null;
      
        const opponentWins = game.totalGamePoints[opponent._id] || 0;
      
        return (
          <div className="rps-opponent-wrapper">
            <div className="rps-opponent-area">
              <div className="rps-opponent-avatar">
                <div className="rps-avatar-gradient-border"></div>
                {renderAvatar(opponent.nickname, opponent.photoUrl, opponent.customAvatar)}
              </div>
              {renderRpsOpponentMessage()}

              <div className="rps-opponent-info">
                <div className="rps-name-and-flag">
                  <span className="rps-opponent-name">{opponent.nickname}</span>
                  {opponent.country && (
                    <img
                      src={getFlag(opponent.country)}
                      alt={`${opponent.country} flag`}
                      className="rps-country-flag"
                    />
                  )}
                </div>
                <span className="rps-opponent-score"> {t('labels.score')}: {opponent.score}</span>
              </div>
      
              <div className="rps-opponent-trophies">
                {Array(3)
                  .fill(0)
                  .map((_, index) => (
                    <div key={index} className="rps-trophy-slot">
                      {index < opponentWins && (
                        <div className="rps-trophy-glow-ellipse"></div>
                      )}
                      <div
                        className="rps-player-trophy-icon"
                        style={{
                        backgroundImage: `url(${index < opponentWins ? commonIcons.trpOn : commonIcons.trp})`,
                        }}
                      ></div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        );
      };
      
    

    // RPS Player Area (like Carrom but adapted)
const renderRPSPlayerArea = (game, user, isCurrentPlayer) => {
    if (!user) return null;
  
    const playerWins = game.totalGamePoints[user._id] || 0;
  
    return (
      <div className={`rps-player-wrapper ${isCurrentPlayer ? 'highlighted waiting-for-move' : ''}`}>
        <div className="rps-player-area">
        {renderRpsPlayerMessage()}
          {/* Avatar with gradient border */}
          <div className="rps-player-avatar">
            <div className="rps-avatar-gradient-border"></div>
            {renderAvatar(user.nickname, user.photoUrl, user.customAvatar)}
          </div>
  
          {/* Info card */}
          <div className="rps-player-info">
            <div className="rps-name-and-flag">
              <span className="rps-player-name">{user.nickname}</span>
              {user.country && (
                <img
                  src={getFlag(user.country)}

                  alt="flag"
                  className="rps-country-flag"
                />
              )}
            </div>
            <span className="rps-player-score">{t('labels.score')}: {user.score}</span>
          </div>
  
          {/* Trophies area */}
          <div className="rps-player-trophies">
            {Array(3)
            .fill(0)
            .map((_, index) => (
                <div key={index} className="rps-trophy-slot">
                {index < playerWins && (
                <div className="rps-trophy-glow-ellipse"></div>
                )}
                <div
                className="rps-player-trophy-icon"
                style={{
                backgroundImage: `url(${index < playerWins ? commonIcons.trpOn : commonIcons.trp})`
                }}
                ></div>
                </div>
                ))}
                </div>
                {renderRpsControlButtons()}
            </div>
      </div>
    );
  };
  

    const renderPopup = () => {
        if (ShowCancelPopup) {
            return (
                <div className="popup">
    <div className="popup-inner">
        <h3>{t('popup.gameCanceled')}</h3>
        <p>{t('popup.cancelReason')}</p>
        <button onClick={() => setShowCancelPopup(false)}>
            {t('buttons.close')}
        </button>
    </div>
</div>
            );
        }


    };

    const renderPlayButtons = (handlePlayerChoice, playerMove, countdown, game) => {
        if (animateRound || isGameEnd) return null;
        
            const options = [
            { name: "rock", icon: rpsAssets.rock },
            { name: "scissors", icon: rpsAssets.scissors },
            { name: "paper", icon: rpsAssets.paper },
          ];
    
        if (playerMove) {
            const currentRoundData = game.roundResults
                .filter(round => round.roundNumber === game.currentRound)
                .pop();
            const winnerId = currentRoundData?.winnerId;
            
            return (
                <div
                    className={`rps-play-choice ${
                        game.phase === "round-completed" ? "rps-animate-to-play-area" : ""
                    }`}
                >
                    <div className={`rps-chosen-icon-wrapper ${playerMove}`}>
                        <img
                            src={rpsAssets[playerMove]}
                            alt={playerMove}
                            className={`rps-chosen-icon ${
                                game.phase === "round-completed" && winnerId === user._id
                                  ? "rps-winner-glow"
                                  : game.phase === "round-completed" && winnerId === null
                                  ? "rps-tie-glow"
                                  : game.phase === "round-completed" && winnerId !== user._id && winnerId !== null
                                  ? "rps-no-animation"
                                  : ""
                            }`}
                        />
                    </div>
                    {game?.phase === "waiting-for-opponent" && (
                        <div className="rps-waiting-message">{t('rps.waitingForOpponent')}</div>
                    )}
                </div>
            );
        }
        
        
        
    
        if (game.phase === "choose" || game.phase === "waiting-for-opponent") {
        return (
            <div className="rps-play-buttons-wrapper">
                {renderCountdown()}
                <div className="rps-play-buttons">
                    {options.map((option) => (
                        <button
                            key={option.name}
                            className="rps-play-button" 
                            onPointerUp ={(e) => handlePlayerChoice(e, option.name)}
                        >
                            <img src={option.icon} alt={option.name} className="rps-play-icon" />
                        </button>
                    ))}
                </div>
            </div>
        );
    }
    };
    
    
    
    const renderCountdown = () => {
        const paddedTime = countdown < 10 ? `0${countdown}` : `${countdown}`;
      
        return (
          <div className="rps-timer-wrapper">
            {countdown > 0 ? (
              <>
                {paddedTime.split('').map((digit, index) => (
                  <div key={index} className="rps-timer-box">
                    {digit}
                  </div>
                ))}
              </>
            ) : (
              <div className="rps-auto-pick-message">{t('rps.autoPicking')}</div>
            )}
          </div>
        );
      };
      


    

    if (!game) {
        return (
            <div className="rps-container">
                <h2>{t('loading.loadingGame')}</h2>
            </div>
        );
    }









    const renderRpsControlButtons = () => {
        if (!game) return null;
      
        return (
          <div className="rps-game-buttonsIcons">
            {/* Settings Button first */}
            <button
              className={`rps-settingsIcon-buttonIcon ${rpsShowSettingsMenu ? "active" : ""}`}
              onPointerDown={(e) => {
                e.stopPropagation();
                setRpsShowSettingsMenu(!rpsShowSettingsMenu);
              }}
            >
              <img src={commonIcons.settingsIcon} alt="Settings" />
            </button>
      
            {/* Expanded Buttons */}
            {rpsShowSettingsMenu && (
              <div className="rps-settings-expanded">
                <button
                  className="rps-leaveGame-buttonExt"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    handleLeaveGame(e);
                  }}
                >
                  <img src={commonIcons.exitIcon} alt="Leave Game" />

                </button>
      
                <button
                  className="rps-soundToggle-buttonExt"
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    handleSoundToggle(e);
                  }}
                >
                  <img src={isSoundOn ? commonIcons.soundIcon : commonIcons.soundOffIcon} alt="Sound" />

                </button>
              </div>
            )}
      
            {/* Text Button second */}
            <button
              className="rps-textIcon-buttonIcon"
              onPointerDown={(e) => {
                e.stopPropagation();
                setRpsShowTextMenu(!rpsShowTextMenu);
              }}
            >
              <img src={commonIcons.textIcon} alt="Text" />

            </button>
      
            {rpsShowTextMenu && (
              <div className="rps-dropdown-menu rps-text-menu">
  <ul>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('hello'); 
    }}>
      {t('quickChat.hello')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('goodLuck'); 
    }}>
      {t('quickChat.goodLuck')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('wellPlayed'); 
    }}>
      {t('quickChat.wellPlayed')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('thanks'); 
    }}>
      {t('quickChat.thanks')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('oops'); 
    }}>
      {t('quickChat.oops')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('tooEasy'); 
    }}>
      {t('quickChat.tooEasy')}
    </li>
    <li onPointerUp={(e) => { 
      e.stopPropagation(); 
      handleRpsMessageSelect('hurryUp'); 
    }}>
      {t('quickChat.hurryUp')}
    </li>
  </ul>
</div>
            )}
          </div>
        );
      };
      
      
    const renderRpsPlayerMessage = () => {
        return (
          rpsPlayerMessage && (
            <div className="rps-message-popup rps-player-message">
              <div className="rps-message-popup-inner rps-player-message-inner">
                {rpsPlayerMessage}
              </div>
            </div>
          )
        );
      };
    
    const renderRpsOpponentMessage = () => {
        return (
          rpsOpponentMessage && (
            <div className="rps-message-popup rps-opponent-message">
              <div className="rps-message-popup-inner rps-opponent-message-inner">
                {rpsOpponentMessage}
              </div>
            </div>
          )
        );
      }
    
      const renderLeaveGamePopup = () => {
        if (!isLeaveGamePopupVisible || isGameEnd) return null;
        return (
          isLeaveGamePopupVisible && (
            <>
            <div className="rps-popup-overlay"></div>
              <div className="rps-screen-blur" />
      
              <div className="rps-end-game-popup">
                <div className="rps-popup-background" />
                <div className="rps-leave-popup-content">
                  <div className="rps-leave-popup-title">
    {t('leaveGame.warning')}
</div>
      
                  <div className="rps-leave-popup-buttons">
                    <button
                      className="rps-leave-popup-btn"
                      onPointerUp={(e) => handleConfirmLeave(e, game?._id)}
                    >
                      {t('buttons.leave')}
                    </button>
                    <button
                      className="rps-leave-popup-btn"
                      onPointerUp={(e) => handlePopupClose(e)}
                    >
                      {t('buttons.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )
        );
    };
      
    const handleRpsMessageSelect = (message) => {
        // Show message for the current player
        setRpsPlayerMessage(t(`quickChat.${message}`));
        setTimeout(() => setRpsPlayerMessage(null), 2500);
    
        // Emit the message to the opponent via WebSocket
        socket.emit('rps-message', { message, gameId: game._id, userID: user._id });
    
        setRpsShowTextMenu(false);
    };
    
    // The following functions can remain the same since they're not carrom-specific
    const handleSoundToggle = (e) => {
        e.stopPropagation();
        setIsSoundOn((prev) => !prev);
    };
    
    const handleLeaveGame = (e) => {
        e.stopPropagation();
        setIsLeaveGamePopupVisible(true);
    };
    
    const handlePopupClose = (e) => {
        e.preventDefault();
        setIsLeaveGamePopupVisible(false);
    };
    
    const handleConfirmLeave = async (e, gameId) => {
        e.preventDefault();
    
        if (!gameId) {
            setIsLeaveGamePopupVisible(false);
            return;
        } 
       
        try {
          
            const response = await apiRequest(`${API_BASE_URL}/api/leave-rps-game`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ gameId }),
            });
    
            const data = await response.json();
    
            if (response.ok) {
                setIsLeaveGamePopupVisible(false);
            } else {
                console.error('Failed to leave game:', data.message);
            }
        } catch (error) {
            console.error('Error leaving game:', error);
        }
    };





  
    return (
        <div className="rps-container">

            
            
        {game && renderOpponentArea(game, user, opponents, currentRound)}
        
            

    <div className="rps-play-area">
 
    {opponentMove && game?.phase === "round-completed" && (
        (() => {
            const currentRoundData = game.roundResults
                .filter((round) => round.roundNumber === game.currentRound)
                .pop();

         
            const winnerId = currentRoundData?.winnerId;
            const opponent = opponents.find(o => o._id !== user._id);
            if (!opponent) return null;


            return (
                <div className="rps-opponent-move-wrapper">
                    <img
                        src={rpsAssets[opponentMove]}

                        alt={opponentMove}
                        className={`rps-opponent-move-icon ${opponentMove} ${
                            game.phase === "round-completed" && winnerId === opponent._id
                              ? "rps-winner-glow"
                              : game.phase === "round-completed" && winnerId === null
                              ? "rps-tie-glow"
                              : game.phase === "round-completed"
                              ? "rps-no-animation"
                              : ""
                          }`}
                          
                    />
                </div>
            );
        })()
    )}
</div>






            <div className="rps-play-buttons-wrapper">
            {renderPlayButtons(handlePlayerChoice, playerMove, countdown, game)}
            </div>

            
           

            <div className="rps-player-wrapper">
            {renderRPSPlayerArea(game, user, currentRound)}
            </div>

           
            {renderGameEndPopup()}
            {renderLeaveGamePopup()}
            {renderGameScore()}
            {renderPopup()}
        </div>
    );
};

export default RPSGame;
